import { useEffect, useId, useState, type ReactNode, useMemo } from 'react';
import NameFilterBar from '../components/NameFilterBar';
import { nachNamenFiltern } from '../utilities/namensfilter';

import { useTranslation } from 'react-i18next';
import { useModalContext } from '../components/ModalProvider';
import { MarketplaceOffer } from '@the-real-insight/tri-model';
import { userAPI } from '../UserAPI';
import { AgentToolset } from '@the-real-insight/tri-model';
import MarketingDetails from './MarketingDetails';
import PlanPanel from './PlanPanel';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { useTestDialog } from './hooks/useTestDialog';
import SampleRequests from './SampleRequests';
import truncate from 'lodash.truncate';
import OfferStatusManagement, { InternallyAvailableCheckbox } from './OfferStatusManagement';
import { withMarketplaceMenuItems } from './marketplacePublishing';
import { useSearchParams } from 'react-router-dom';
import { useUrlPageState } from '../utilities/dataTablePagination';
import { FluentIconSelector } from '../components/FluentIconSelector';
import { ColorSelector } from '../components/ColorSelector';
import MCPTestDialog from './MCPTestDialog';
import { mcpAPI } from '../UserAPI';
import ConfigurationSchemaEditor, { ConfigurationParameter } from './ConfigurationSchemaEditor';
import ToolConfigurationForm from './ToolConfigurationForm';
import { OfferRowMenu, YesNoBadge, rowMenuColumnMeta } from "./components/offerTable";
import { MarketplaceOfferNameCell } from './components/MarketplaceOfferNameCell';

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    AlertCircle,
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Check,
    Loader2,
    Lock,
    Plus,
    Rocket,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                : sorted === 'desc'
                    ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

interface AgentToolsetProps {
}

export default function AgentToolsetManagement(_props: AgentToolsetProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [searchParams, setSearchParams] = useSearchParams();
    const agentToolsetPagination = useUrlPageState('agentToolsetPage');
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog, closeTestDialog: _closeTestDialog } = useTestDialog();
    const [agentToolsets, setAgentToolsets] = useState<any[]>([]);
    /* Suchfeld über der Tabelle: Teilzeichenkette im Namen, siehe utilities/namensfilter.ts. */
    const [toolsetFilter, setToolsetFilter] = useState('');
    const gefilterteAgentToolsets = useMemo(
        () => nachNamenFiltern(agentToolsets, toolsetFilter),
        [agentToolsets, toolsetFilter]
    );
    // Starts true: the first frame shows the table skeleton, not an empty-state flash.
    const [loadingAgentToolsets, setLoadingAgentToolsets] = useState(true);
    const [selectedAgentToolset, setSelectedAgentToolset] = useState<any>(null);
    const initialKey = searchParams.get('key') || 'server'; // Default to MCP Server tab
    const [selectedKey, setSelectedKey] = useState<any>(initialKey);
    const [mcpTestDialogOpen, setMcpTestDialogOpen] = useState(false);
    const [mcpConnectionStatus, setMcpConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'error' | 'requiresAuth'>('idle');
    const [mcpConnectionError, setMcpConnectionError] = useState<string | null>(null);
    const [mcpAuthUrl, setMcpAuthUrl] = useState<string | null>(null);
    const [toolConfigValues, setToolConfigValues] = useState<Record<string, string>>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const menuItems = withMarketplaceMenuItems([
        { key: 'general', name: t('ui.general') },
        { key: 'offerStatus', name: t('ui.offerStatus') },
        { key: 'server', name: t('ui.mcpServer') },
        { key: 'configuration', name: t('ui.configuration') },
        { key: 'sampleRequests', name: t('ui.sampleRequests') },
        { key: 'details', name: t('ui.details') },
        { key: 'billing', name: t('ui.billing') }
    ], authentication?.customerObject);

    // Get valid menu keys for this tool type
    const validMenuKeys = menuItems.map(item => item.key);

    const deleteAgentToolset = (server: any) => {
        showDeleteDialog(`${t('ui.deleteMCPServer')} ${server.name}`,
            server.name,
            async () => {
                await userAPI.deleteMarketplaceOffer(authentication.customer, server);
            });
    };

    const agentToolsetColumns: ColumnDef<any>[] = [{
        id: 'name',
        size: 320,
        accessorFn: (server: any) => server.name,
        header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
        cell: ({ row }) => <MarketplaceOfferNameCell offer={row.original} />
    }, {
        id: 'description',
        size: 300,
        header: plainHeader('Beschreibung'),
        cell: ({ row }) => <div className="line-clamp-2 min-w-0 break-words text-muted-foreground">{truncate(row.original.synopsis, { length: 200, separator: /,? +/ })}</div>
    }, {
        id: 'published',
        size: 160,
        accessorFn: (server: any) => server.published,
        header: ({ column }) => <SortableHeader column={column}>Intern verfügbar</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.published)} />
    }, {
        id: 'offered',
        size: 180,
        accessorFn: (server: any) => server.offered,
        header: ({ column }) => <SortableHeader column={column}>Im Marktplatz angeboten</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.offered)} />
    }, {
        id: 'serverURL',
        size: 300,
        header: plainHeader('Server URL'),
        cell: ({ row }) => <div className="min-w-0 break-all font-mono text-xs text-muted-foreground">{row.original.serverURL}</div>
    }, {
        id: 'rowMenu',
        size: 56,
        header: () => <span className="sr-only">{t('ui.actions', 'Aktionen')}</span>,
        meta: rowMenuColumnMeta,
        cell: ({ row }) => (
            <OfferRowMenu
                onEdit={() => selectAgentToolset(row.original)}
                onTest={() => openTestDialog(row.original)}
                onDelete={() => deleteAgentToolset(row.original)}
            />
        ),
    }];

    const onImagesChange = async (imageList: any, _addUpdateIndex: any) => {
        try {
            if (!imageList?.[0]?.file) {
                return;
            }
            // TODO put in API function
            const form = new FormData();
            form.append("file", imageList[0].file);
            const image = await userAPI.uploadFile(form);
            console.log('image', image);
            updateSelectedAgentToolset({
                ...selectedAgentToolset,
                images: [...(selectedAgentToolset.images || []), image.path]
            });
        } catch (error: any) {
            pushError(error);
        }
    };

    const loadAgentToolsets = async () => {
        try {
            setLoadingAgentToolsets(true);

            const offers = await userAPI.getMarketplaceOffers(authentication.customer);
            const agentToolsets = offers.filter((o: any) => o._type === AgentToolset.type.name);

            setAgentToolsets(agentToolsets);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingAgentToolsets(false);
        }
    }

    const updateSelectedAgentToolset = (agentToolset: any) => {
        if (!agentToolset) {
            setSelectedAgentToolset(null);
            return;
        }
        const newAgentToolset = new AgentToolset(agentToolset);
        Object.assign(newAgentToolset, agentToolset);
        setSelectedAgentToolset(newAgentToolset);
        setHasUnsavedChanges(true);
    }

    const selectAgentToolset = (agentToolset: any) => {
        // Selecting, opening or closing a toolset starts from a clean slate.
        setHasUnsavedChanges(false);
        if (!agentToolset) {
            console.log('🔧 TOOLSET - Closing toolset, cleaning up URL parameters');
            setSelectedAgentToolset(null);
            setToolConfigValues({});
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('agentToolset');
            nextSearchParams.delete('key');
            setSearchParams(nextSearchParams);
        } else {
            const newAgentToolset = new AgentToolset(agentToolset);
            Object.assign(newAgentToolset, agentToolset);
            setSelectedAgentToolset(newAgentToolset);
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set('agentToolset', agentToolset._id);
            nextSearchParams.delete('knowledgeBase');
            nextSearchParams.delete('promptTool');
            setSearchParams(nextSearchParams);
        }
    };

    const loadAgentToolsetFromURL = async () => {
        const toolsetId = searchParams.get('agentToolset');

        if (toolsetId && agentToolsets.length > 0) {
            const agentToolset = agentToolsets.find(at => at._id === toolsetId);
            if (agentToolset) {
                const newAgentToolset = new AgentToolset(agentToolset);
                Object.assign(newAgentToolset, agentToolset);
                setSelectedAgentToolset(newAgentToolset);
            }
        }
    };

    const testMCPConnection = async (serverURL: string) => {
        if (!serverURL || serverURL.trim() === '') {
            setMcpConnectionStatus('idle');
            setMcpConnectionError(null);
            return;
        }

        setMcpConnectionStatus('connecting');
        setMcpConnectionError(null);

        try {
            const client = mcpAPI.createClient(serverURL);
            await client.connect();
            if (client.authUrl) {
                setMcpAuthUrl(client.authUrl);
                setMcpConnectionStatus('requiresAuth');
                return;
            }
            setMcpAuthUrl(null);
            await client.listTools();
            setMcpConnectionStatus('connected');
            setMcpConnectionError(null);
        } catch (error: any) {
            console.error('MCP connection test failed:', error);
            setMcpConnectionStatus('error');
            setMcpConnectionError(error.message || t('ui.connectionFailed'));
        }
    };

    // Handle URL parameter changes and key validation
    useEffect(() => {
        const keyParam = searchParams.get('key') || 'server'; // Default to MCP Server tab

        // Only update if the key has actually changed and is valid
        if (keyParam !== selectedKey) {
            if (validMenuKeys.includes(keyParam)) {
                setSelectedKey(keyParam);
            } else {
                setSelectedKey('server'); // Fallback to MCP Server tab

                // Update URL to reflect the fallback
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('key', 'server');
                setSearchParams(newSearchParams, { replace: true });
            }
        }
    }, [searchParams, validMenuKeys]); // Removed selectedKey from dependencies

    // Sync selectedAgentToolset with URL — clear selection when param is removed (e.g. browser back)
    useEffect(() => {
        const toolsetId = searchParams.get('agentToolset');
        if (!toolsetId && selectedAgentToolset) {
            setSelectedAgentToolset(null);
            setToolConfigValues({});
        } else if (toolsetId && agentToolsets.length > 0 && selectedAgentToolset?._id !== toolsetId) {
            loadAgentToolsetFromURL();
        }
    }, [searchParams, agentToolsets]);

    useEffect(() => {
        const call = async () => {
            if (!selectedAgentToolset) {
                loadAgentToolsets();
                setMcpConnectionStatus('idle');
                setMcpConnectionError(null);
            } else if (selectedAgentToolset.serverURL) {
                // Test connection when toolset is selected and has a server URL
                await testMCPConnection(selectedAgentToolset.serverURL);
            }
        }

        call();
    }, [selectedAgentToolset]);

    // Load tool configuration when offer has _id
    useEffect(() => {
        const load = async () => {
            if (!selectedAgentToolset?._id || !authentication?.customer) {
                setToolConfigValues({});
                return;
            }
            try {
                const config = await userAPI.getToolConfiguration(authentication.customer, selectedAgentToolset._id);
                setToolConfigValues(config?.schemaConfiguration ?? {});
            } catch {
                setToolConfigValues({});
            }
        };
        load();
    }, [selectedAgentToolset?._id, authentication?.customer]);

    return (
        <>
            {selectedAgentToolset
                ?
                <div className="min-w-0 rounded-md border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="min-w-0">
                            <div className="text-sm">{t('ui.agentToolset')}</div>
                            <div className="break-words text-base font-semibold">{selectedAgentToolset.name}</div>
                        </div>
                        <div className="flex flex-1 flex-wrap items-center justify-end gap-4">
                            {hasUnsavedChanges && (
                                <Badge variant="warning" className="gap-1 whitespace-nowrap">
                                    <AlertTriangle className="h-3 w-3" aria-hidden />
                                    {t('global.unsavedChanges')}
                                </Badge>
                            )}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            aria-label={t('ui.test')}
                                            onClick={() => openTestDialog(selectedAgentToolset)}
                                        >
                                            <Rocket className="h-4 w-4" aria-hidden />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('ui.test')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <Button type="button" variant="outline" onClick={() => {
                                selectAgentToolset(null);
                            }}>{t('ui.cancel')}</Button>
                            {selectedAgentToolset._id ?
                                <Button type="button" onClick={async () => {
                                    const offerToSave = { ...selectedAgentToolset, configurationSchema: selectedAgentToolset.configurationSchema ?? [] };
                                    await userAPI.updateMarketplaceOffer(authentication.customer, offerToSave);
                                    if (Object.keys(toolConfigValues).length > 0) {
                                        const strValues: Record<string, string> = {};
                                        for (const [k, v] of Object.entries(toolConfigValues)) {
                                            strValues[k] = String(v ?? '');
                                        }
                                        await userAPI.putToolConfiguration(
                                            authentication.customer,
                                            selectedAgentToolset._id,
                                            strValues,
                                            selectedAgentToolset.configurationSchema ?? []
                                        );
                                    }
                                    await loadAgentToolsets();
                                    selectAgentToolset(null);
                                }}>{t('ui.save')}</Button>
                                :
                                <Button type="button" onClick={async () => {
                                    const offerToSave = { ...selectedAgentToolset, configurationSchema: selectedAgentToolset.configurationSchema ?? [] };
                                    const created = await userAPI.createMarketplaceOffer(authentication.customer, offerToSave) as any;
                                    if (created?._id && Object.keys(toolConfigValues).length > 0) {
                                        const strValues: Record<string, string> = {};
                                        for (const [k, v] of Object.entries(toolConfigValues)) {
                                            strValues[k] = String(v ?? '');
                                        }
                                        await userAPI.putToolConfiguration(
                                            authentication.customer,
                                            created._id,
                                            strValues,
                                            selectedAgentToolset.configurationSchema ?? []
                                        );
                                    }
                                    await loadAgentToolsets();
                                    selectAgentToolset(null);
                                }}>{t('ui.create')}</Button>
                            }
                        </div>
                    </div>
                    <div className="mt-3 flex min-w-0 flex-wrap gap-4 rounded-md border p-3">
                        <div className="w-[200px] shrink-0">
                            {menuItems.map((item) => (
                                <button
                                    type="button"
                                    key={item.key}
                                    className={cn(
                                        'flex w-full items-center rounded-md p-3 text-left text-sm hover:bg-accent',
                                        selectedKey === item.key && 'bg-muted'
                                    )}
                                    onClick={() => {
                                        setSelectedKey(item.key);
                                        // Update URL parameter when key changes
                                        const newSearchParams = new URLSearchParams(searchParams);
                                        newSearchParams.set('key', item.key);
                                        setSearchParams(newSearchParams, { replace: true });
                                        // Test MCP connection when switching to server tab
                                        if (item.key === 'server' && selectedAgentToolset?.serverURL) {
                                            testMCPConnection(selectedAgentToolset.serverURL);
                                        }
                                    }}>
                                    {item.name}
                                </button>
                            ))}
                        </div>
                        {/* A flex item defaults to min-width:auto and refuses to shrink below
                            its content, so a wide child (e.g. the billing table) would stretch
                            this column past the card instead of scrolling inside it. */}
                        <div className="min-w-0 flex-1">
                            {(() => {
                                switch (selectedKey) {
                                    case 'general':
                                        return (
                                            <div className="max-w-[600px]">
                                                <div className="flex flex-wrap gap-6">
                                                    <div className="min-w-0 flex-1 basis-[300px]">
                                                        <div className="flex flex-col gap-1.5">
                                                            <Label htmlFor={`${fieldId}-name`}>Name</Label>
                                                            <Input
                                                                id={`${fieldId}-name`}
                                                                value={selectedAgentToolset.name ?? ''}
                                                                onChange={(event) => {
                                                                    updateSelectedAgentToolset({ ...selectedAgentToolset, name: event.target.value });
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="mt-3 flex flex-col gap-1.5">
                                                            <Label htmlFor={`${fieldId}-description`}>Beschreibung</Label>
                                                            <Textarea
                                                                id={`${fieldId}-description`}
                                                                rows={15}
                                                                value={selectedAgentToolset.description ?? ''}
                                                                onChange={(event) => {
                                                                    updateSelectedAgentToolset({ ...selectedAgentToolset, description: event.target.value });
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex min-w-0 flex-wrap gap-4">
                                                        <FluentIconSelector
                                                            label="Icon"
                                                            selectedIcon={selectedAgentToolset.icon || 'CityNext'}
                                                            iconColor={selectedAgentToolset.iconColor}
                                                            onIconChange={(iconName) => {
                                                                updateSelectedAgentToolset({ ...selectedAgentToolset, icon: iconName });
                                                            }}
                                                        />
                                                        <ColorSelector
                                                            label="Icon Color"
                                                            selectedColor={selectedAgentToolset.iconColor}
                                                            onColorChange={(color) => {
                                                                updateSelectedAgentToolset({ ...selectedAgentToolset, iconColor: color });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <InternallyAvailableCheckbox
                                                            marketplaceOffer={selectedAgentToolset}
                                                            updateSelectedMarketplaceOffer={updateSelectedAgentToolset}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    case 'offerStatus':
                                        return (
                                            <OfferStatusManagement
                                                marketplaceOffer={selectedAgentToolset}
                                                updateSelectedMarketplaceOffer={updateSelectedAgentToolset}
                                            />
                                        );
                                    case 'configuration':
                                        return (
                                            <div className="flex flex-col gap-6">
                                                <div>
                                                    <div className="mb-2 text-sm font-semibold">{t('ui.configurationSchema')}</div>
                                                    <ConfigurationSchemaEditor
                                                        value={selectedAgentToolset.configurationSchema || []}
                                                        onChange={(params: ConfigurationParameter[]) => {
                                                            updateSelectedAgentToolset({
                                                                ...selectedAgentToolset,
                                                                configurationSchema: params,
                                                            });
                                                        }}
                                                    />
                                                </div>
                                                {(selectedAgentToolset.configurationSchema?.length ?? 0) > 0 && (
                                                    <div>
                                                        <div className="mb-2 text-sm font-semibold">{t('ui.toolConfigurationValues')}</div>
                                                        <ToolConfigurationForm
                                                            schema={selectedAgentToolset.configurationSchema}
                                                            value={toolConfigValues}
                                                            onChange={(v: any) => { setToolConfigValues(v); setHasUnsavedChanges(true); }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    case 'server':
                                        return (
                                            <div className="flex max-w-[600px] flex-col gap-4">
                                                <div className="flex flex-wrap items-end gap-4">
                                                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                                        <Label htmlFor={`${fieldId}-serverURL`}>{t('ui.mcpServerURL')}</Label>
                                                        <Input
                                                            id={`${fieldId}-serverURL`}
                                                            value={selectedAgentToolset.serverURL ?? ''}
                                                            onChange={(event) => {
                                                                updateSelectedAgentToolset({ ...selectedAgentToolset, serverURL: event.target.value });
                                                                // Reset connection status when URL changes
                                                                setMcpConnectionStatus('idle');
                                                                setMcpConnectionError(null);
                                                            }}
                                                            onBlur={() => {
                                                                if (selectedAgentToolset.serverURL) {
                                                                    testMCPConnection(selectedAgentToolset.serverURL);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {mcpConnectionStatus === 'connecting' && (
                                                        <div className="flex items-center">
                                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                                        </div>
                                                    )}
                                                    {mcpConnectionStatus === 'connected' && (
                                                        <div className="flex items-center gap-2">
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="flex items-center">
                                                                            <Check className="h-5 w-5 text-success" aria-hidden />
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Connected successfully</TooltipContent>
                                                                </Tooltip>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            aria-label={t('ui.testMCPServer')}
                                                                            onClick={() => setMcpTestDialogOpen(true)}
                                                                        >
                                                                            <Rocket className="h-4 w-4" aria-hidden />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{t('ui.testMCPServer')}</TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                    )}
                                                    {mcpConnectionStatus === 'error' && (
                                                        <div className="flex items-center gap-2">
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="flex items-center">
                                                                            <AlertCircle className="h-5 w-5 text-destructive" aria-hidden />
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{mcpConnectionError || t('ui.connectionFailed')}</TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                    )}
                                                    {mcpAuthUrl && (
                                                        <div className="flex items-center gap-2">
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="flex items-center">
                                                                            <Lock className="h-5 w-5 text-primary" aria-hidden />
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Authorization required</TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                            <Button type="button" variant="outline" onClick={() => {
                                                                window.open(mcpAuthUrl, '_blank', 'noopener,noreferrer');
                                                            }}>{t('ui.authorize')}</Button>
                                                        </div>
                                                    )}
                                                </div>
                                                {mcpConnectionError && (
                                                    <div className="mt-1 text-xs text-destructive">
                                                        {mcpConnectionError}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    case 'sampleRequests':
                                        return (
                                            <SampleRequests
                                                type={AgentToolset.type}
                                                marketplaceOffer={selectedAgentToolset as MarketplaceOffer}
                                                setMarketplaceOffer={updateSelectedAgentToolset}
                                            />
                                        );
                                    case 'details':
                                        return (
                                            <MarketingDetails
                                                type={AgentToolset.type}
                                                marketplaceOffer={selectedAgentToolset as MarketplaceOffer}
                                                setMarketplaceOffer={selectAgentToolset}
                                                onImagesChange={onImagesChange}
                                            />
                                        );
                                    case 'billing':
                                        return (
                                            <PlanPanel
                                                type={AgentToolset.type}
                                                marketplaceOffer={selectedAgentToolset as MarketplaceOffer}
                                                setMarketplaceOffer={updateSelectedAgentToolset}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })()}
                        </div>
                    </div>
                </div>
                :
                <div className='mt-4 min-w-0'>
                    <div className="flex flex-wrap items-center gap-3">
                        <NameFilterBar
                            wert={toolsetFilter}
                            onChange={setToolsetFilter}
                            seite={agentToolsetPagination.page}
                            onSeiteChange={agentToolsetPagination.onPageChange}
                            anzahl={gefilterteAgentToolsets.length}
                            gesamt={(agentToolsets ?? []).length}
                        />
                        <Button
                            data-guide-id="settings.agent.toolset.create"
                            type="button"
                            variant="ghost"
                            onClick={async () => {
                                const newAgentToolset = new AgentToolset({
                                    name: t('ui.newAgentToolset'),
                                    serverURL: '',
                                    icon: 'CityNext',
                                    iconColor: '#0078d4',
                                });
                                selectAgentToolset(newAgentToolset);
                            }}
                        >
                            <Plus className="mr-2 h-4 w-4" aria-hidden />
                            {t('ui.addAgentToolset')}
                        </Button>
                    </div>
                    <div className='mt-4 overflow-x-auto'>
                        <DataTable
                            data={gefilterteAgentToolsets}
                            columns={agentToolsetColumns}
                            page={agentToolsetPagination.page}
                            onPageChange={agentToolsetPagination.onPageChange}
                            loading={loadingAgentToolsets && agentToolsets.length === 0}
                            onRowClick={selectAgentToolset}
                            emptyMessage={<div className='p-6'>{t('ui.noToolsetsFound')}</div>}
                        />
                    </div>
                </div>
            }
            {TestDialog}
            <MCPTestDialog
                isOpen={mcpTestDialogOpen}
                onDismiss={() => setMcpTestDialogOpen(false)}
                serverURL={selectedAgentToolset?.serverURL || ''}
            />
        </>
    );
}
